/**
 * Authors: mariushirjanu
 * Created: 30/12/2020
 */
import _ from 'underscore';
import { util } from '@app/shared/util';
import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import { LOV_DOCTYPE, LovService } from '../services/lov.service';
import { FormattingService } from '../services/formatting.service';
import { TranslateService } from '@ngx-translate/core';
import { IFileUpload, IFileUploadFailResponse } from './file-upload.service';
import { GaMessageService } from '../message/ga-message.service';
import { ICodeValue, IDocument } from '@type_definitions/datatypes';
import { DocumentDataService, DocumentTableReference } from '@app/shared/document/document-data.service';

interface ICodeDescription {
  code:string;
  description:string;
}

interface IUploadFormData {
  doc_type_code:string;
  doc_desc:string;
  tableReference?:DocumentTableReference;
  recordId?:number;
}

interface IFileDialogModel {
  description?:string;
  documentType?:ICodeValue;
  fileName?:string;
}

@Component({
  selector: 'file-upload-dialog',
  template: `
    <div class="modal-header">
      <h4 class="modal-title float-start" translate>
        <span *ngIf="title" translate>{{title}}</span>
        <span *ngIf="!title" [translateParams]="{label: documentLabel}" translate>{{ document?.id? 'GA.BUTTONS.EDIT_LABEL' : 'GA.BUTTONS.ADD_LABEL' }}</span>
      </h4>

      <button type="button" class="btn-close float-end" aria-label="Close" (click)="cancel()"></button>
    </div>
    <div class="modal-body">
      <formly [form]="form" [fields]="fields" [model]="model"></formly>
      <div *ngIf="progress" class="progress">
        <div class="progress-bar" role="progressbar" [attr.aria-valuenow]="progress" aria-valuemin="0" aria-valuemax="100" [style.width.%]="progress" [translateParams]="{percentage: progress}" translate>GA.GENERAL.PROGRESS</div>
      </div>
    </div>
    <div class="modal-footer">
      <span *ngIf="!document?.id">
        <file-button [autoSelect]="autoselect" [url]="uploadUrl" [formData]="uploadFormData" [submitting]="submitting" [uploading]="uploading" (onAfterSubmit)="onAfterSubmit($event)" (onFileAdd)="onFileAdd($event)" (onUploadStarted)="onUploadStarted()" (onUploadProgress)="onUploadProgress($event)" (onUploadDone)="onUploadDone($event)"
          (onUploadFailed)="onUploadFailed($event)">
          <i class="fa fa-folder-open-o"></i>&nbsp;<span [translateParams]="{label: documentLabel}" translate>GA.BUTTONS.CHOOSE_LABEL</span>
        </file-button>
        &nbsp; &nbsp; &nbsp; &nbsp;
      </span>

      <button *ngIf="!document?.id" class="btn btn-success" (click)="onAdd()" [busyButton]="uploading" [disabled]="!model?.fileName" [translateParams]="{label: documentLabel}" translate>GA.BUTTONS.ADD_LABEL</button>&nbsp;
      <button *ngIf="document?.id" class="btn btn-success" (click)="onEdit()" [busyButton]="submitting" [translateParams]="{label: documentLabel}" translate>GA.BUTTONS.SAVE_LABEL</button>&nbsp;
      <button class="btn btn-danger" (click)="cancel()" translate>{{ document?.id? 'GA.BUTTONS.NO_SAVE' : 'GA.BUTTONS.NO_ADD' }}</button>
    </div>
  `
})
export class FileUploadDialogComponent implements OnInit {
  @Input() public title:string;
  @Input() public label:string;
  @Input() public documentType?:string;
  @Input() public hideDocumentType?:boolean;
  @Input() public editableDocumentType?:boolean;
  @Input() public document:IDocument;
  @Input() public uploadUrl:string;
  @Input() public autoselect:boolean;
  @Input() public tableReference?:DocumentTableReference;
  @Input() public recordId?:number;
  @Output() public onClose:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onSave:EventEmitter<IDocument> = new EventEmitter<IDocument>();

  public submitting:boolean;
  public uploading:boolean;
  public saving:boolean;
  public uploadFormData:IUploadFormData;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IFileDialogModel = {};
  public documentLabel:string;
  public progress:number;

  private documentTypeField:IExtraFieldConfigurationObject;
  private descriptionField:IExtraFieldConfigurationObject;
  private abortFn:Function;
  private uploadingStatusMessage:Noty;

  constructor(
    private documentDataService:DocumentDataService,
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private lovService:LovService,
    private translateService:TranslateService,
    private gaMessageService:GaMessageService
  ) {
    /***/
  }

  public ngOnInit():void {
    this.documentLabel = this.label || this.translateService.instant('GA.GENERAL.DOCUMENT');
    if(this.document){
      this.model = {
        description: this.document.description,
        fileName: this.document.fileName,
        documentType: this.document.documentType
      };
    }

    this.fields = this.createFields();
    this.fillFields();
  }

  public ngOnChanges(changes:SimpleChanges):void {
    // editableDocumentType = true when adding a new document and false when editing; however this logic can be overwritten by specifying editableDocumentType
    if (changes.editableDocumentType) {
      this.editableDocumentType = util.notNullOrUndefined(changes.editableDocumentType?.currentValue) ? changes.editableDocumentType.currentValue : !(this.model?.documentType?.code || this.documentType);
    }
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-md-12';
    const valueClass:string = 'col-md-12';

    const form:IExtraFieldConfigurationObject[] = [];

    if(!this.hideDocumentType) {
      if (this.editableDocumentType) {
        form.push(
          this.documentTypeField = this.formlyService.aSelect('documentType', 'GA.GENERAL.TYPE')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withFormat(this.formattingService.formatLov)
          .withTrackBy('code')
          .build()
        );
      } else {
        form.push(
          this.formlyService.aValue('documentType', 'GA.GENERAL.TYPE')
          .withLabelClass(labelClass)
          .withValueClass(valueClass)
          .withFormat(this.formattingService.formatLov)
          .build()
        );
      }
    }

    form.push(
      this.descriptionField = this.formlyService.anInput('description', 'GA.GENERAL.DESCRIPTION')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    );

    form.push(
      this.formlyService.aValue('fileName', 'GA.GENERAL.NAME')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build()
    );

    return form;
  }

  private fillFields():void {
    if(this.documentTypeField) {
      this.formlyService.fillField(this.documentTypeField, this.lovService.list(LOV_DOCTYPE))
        .then((documentTypes:ICodeDescription[]) => _.sortBy(documentTypes, 'description')); // documentTypes types by description
    }
  }

  public onFileAdd(file:File):void {
    if (!this.descriptionField.formControl.touched || !this.model.description) {
      this.form.patchValue({ description: file?.name });
    }
    this.form.patchValue({ fileName: file?.name });
  }

  public onAdd():void {
    if (this.model.fileName) {
      this.uploadFormData = {
        doc_type_code: this.model?.documentType?.code,
        doc_desc: this.model?.description || this.model.fileName,
        tableReference: this.tableReference,
        recordId: this.recordId
      };

      this.uploadFormData = util.deepClone(this.uploadFormData); // remove undefined

      this.submitting = true;
      this.uploading = true;
    }
  }

  public onAfterSubmit(submitHandler:XMLHttpRequest):void {
    this.abortFn = submitHandler.abort;
    this.submitting = false;
  }

  public onUploadStarted():void {
    this.uploadingStatusMessage = this.gaMessageService.showStatusMessage(`Uploading document ${this.model.fileName}`);
  }

  public onUploadProgress(progress:number):void {
    this.progress = progress;
  }

  public onUploadDone(result:IFileUpload):void {
    this.gaMessageService.closeMessage(this.uploadingStatusMessage);
    this.uploading = false;

    if (result?.status === 'success') {
      this.onSave.emit(<any>result?.response?.data);
    } else {
      this.uploadFailed();
    }
  }

  public onUploadFailed(response:IFileUploadFailResponse):void {
    this.uploadFailed(response);
  }

  private uploadFailed(response?:IFileUploadFailResponse):void {
    this.gaMessageService.closeMessage(this.uploadingStatusMessage);
    if (response) {
      let errorMessage:string = `File upload failed: ${this.model.fileName}`;
      if (response.status) {
        errorMessage += ` (${response.status}${response.error? ' ' + response.error : ''})`;
        this.gaMessageService.showErrorMessage(errorMessage);
      }
    }
    this.onClose.emit();
  }

  public onEdit():void {
    const updatedDocument:IDocument = _.extend(_.clone(this.document), this.model);
    this.submitting = true;
      this.documentDataService.updateDocument(updatedDocument)
      .then((document:IDocument) => {
        this.onSave.emit(document);
      })
      .finally(() => {
        this.submitting = false;
      });
  }

  public cancel = ():void => {
    this.abortFn && this.abortFn();
    this.progress = undefined;
    this.gaMessageService.closeMessage(this.uploadingStatusMessage);
    this.onClose.emit();
  };
}